import {
  LoadingBlock,
  Table,
  TableColumnHeader,
  TableColumnHeaderInfo,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import cx from 'classnames'
import { isNil } from 'ramda'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'

import FiltersModal from '../../components/FiltersModal/FiltersModal'
import InfiniteListWrapper from '../../components/InfiniteListWrapper/InfiniteListWrapper'
import SmallThumb from '../../components/SmallThumb/SmallThumb'
import { SortButton } from '../../components/SortButton/SortButton'
import { TrainingTestOutcomesParams } from '../../store/store.types'
import { TrainingTestOutcome } from '../../types'
import { SortingDirection } from '../../types/sorting'
import useStyles from './AnomalyTestOutcomesList.styles'
import { DownloadOutcomesButton } from './DownloadOutcomesButton'
import IsCorrectFilter from './IsCorrectFilter'
import PromoteAnnotationInLineButton from './PromoteAnnotationButton/PromoteAnnotationInLineButton'
import { useFetchTestOutcomes } from './useFetchTestOutcomes'

type AnomalyTestOutcomesListProps = {
  trainingTestId: number
  trainingTestOutcomesParams: TrainingTestOutcomesParams
  setTrainingTestOutcomesParams: (params: TrainingTestOutcomesParams) => void
}

type Columns = TrainingTestOutcome & {
  anomaly_score?: never
  actions?: string
}

type Sorting = { [key in keyof Partial<Columns>]: SortingDirection }
type Filtering = {
  [key in keyof Partial<Columns>]: string | number | boolean | undefined
}

const useColumns = ({
  trainingTestId,
  sorting,
  handleSort,
}: {
  trainingTestId: number
  sorting: Sorting
  handleSort: (key: keyof Columns, direction: SortingDirection) => void
}) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const columns: TableColumnInstance<Columns, keyof Columns>[] = [
    {
      id: 'id',
      header: 'id',
      template: ({ observationAnnotation: { observation } }) =>
        `#${observation.id}`,
    },
    {
      id: 'observationAnnotation',
      header: t('Image'),
      template: ({ observationAnnotation: { observation }, predMask }) => (
        <SmallThumb
          src={predMask || observation.thumbnail}
          largeSrc={observation.large}
          alt={observation.filename}
          closeButtonLabel={t('Close image')}
          predMask={predMask}
        />
      ),
    },
    {
      id: 'trainingTest',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Filename')}
            sorting={
              <SortButton
                sort={sorting?.filename}
                onSort={(direction) => handleSort('filename', direction)}
              />
            }
          />
        </div>
      ),
      template: ({ observationAnnotation: { observation } }) => (
        <div className={classes.colFilename} title={observation.filename}>
          {observation.filename}
        </div>
      ),
    },
    {
      id: 'expectation',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Expectation')}
            sorting={
              <SortButton
                sort={sorting?.expectation}
                onSort={(direction) => handleSort('expectation', direction)}
              />
            }
          />
        </div>
      ),
      template: ({ expectation }) => expectation,
    },
    {
      id: 'prediction',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Prediction')}
            sorting={
              <SortButton
                sort={sorting?.prediction}
                onSort={(direction) => handleSort('prediction', direction)}
              />
            }
          />
        </div>
      ),
      template: ({ prediction }) => prediction,
    },
    {
      id: 'isCorrect',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Outcome')}
            sorting={
              <SortButton
                sort={sorting?.isCorrect}
                onSort={(direction) => handleSort('isCorrect', direction)}
              />
            }
          />
        </div>
      ),
      template: useCallback(
        (outcome) => (
          <span
            className={cx(
              classes.result,
              outcome.isCorrect ? classes.true : classes.false
            )}
          />
        ),
        [classes.false, classes.result, classes.true]
      ),
    },
    {
      id: 'anomaly_score',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Score')}
            info={
              <div className={classes.sortableColumnHeaderInfo}>
                <TableColumnHeaderInfo>
                  {t(
                    `Score given to the input image by the anomaly model. If this score is above the threshold value, then the image is considered anomaly, normal otherwise.`
                  )}
                </TableColumnHeaderInfo>
              </div>
            }
            sorting={
              <SortButton
                sort={sorting?.anomaly_score}
                onSort={(direction) => handleSort('anomaly_score', direction)}
              />
            }
          />
        </div>
      ),
      template: ({ metadata }) =>
        (metadata &&
          !isNil(metadata.anomalyScore) &&
          metadata.anomalyScore >= 0 &&
          metadata.anomalyScore.toFixed(3)) ||
        '',
    },
    {
      id: 'actions',
      header: t('Actions'),
      template: useCallback(
        (outcome) => (
          <PromoteAnnotationInLineButton
            outcomeId={outcome.id}
            promoted={
              outcome.latestObservationAnnotation.promotedFrom?.id ===
              outcome.observationAnnotation.id
            }
            disabled={
              outcome.expectation === outcome.prediction ||
              outcome.latestObservationAnnotation.promotedFrom?.id ===
                outcome.observationAnnotation.id
            }
          />
        ),
        []
      ),
    },
  ]

  return columns
}

export const AnomalyTestOutcomesList = ({
  trainingTestId,
  trainingTestOutcomesParams,
  setTrainingTestOutcomesParams,
}: AnomalyTestOutcomesListProps) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const [sorting, setSorting] = useState<Sorting>({})
  const [filtering, setFiltering] = useState<Filtering>({})

  const {
    testOutcomes,
    hasMore,
    isEmpty,
    loadTestOutcomes,
  } = useFetchTestOutcomes({ trainingTestId, filtering, sorting })

  const handleSort = (key: keyof Columns, direction: SortingDirection) => {
    setSorting((prev) => ({ ...prev, [key]: direction }))
  }

  const columns = useColumns({ trainingTestId, sorting, handleSort })

  return (
    <div className={classes.container}>
      <div className={classes.topBar}>
        <FiltersModal<{ isCorrect: boolean }>
          labels={{ isCorrect: t('Prediction') }}
          onSave={(filters) =>
            setFiltering((prev) => ({ ...prev, isCorrect: filters.isCorrect }))
          }
          onRemove={() => {
            setTrainingTestOutcomesParams({})
            setFiltering({})
          }}
          removeDisabled={Object.keys(trainingTestOutcomesParams).length === 0}
        >
          {({ filters, setFilters }) => (
            <IsCorrectFilter
              initialValue={filters.isCorrect}
              onChange={(v) =>
                setFilters((prev) => ({
                  ...prev,
                  isCorrect: v?.value ? v.value === 'correct' : undefined,
                }))
              }
            />
          )}
        </FiltersModal>
        <DownloadOutcomesButton trainingTestId={trainingTestId} />
      </div>
      <div className={classes.listWrapper}>
        <InfiniteListWrapper>
          {({ stickedHeader }) => (
            <InfiniteScroll
              pageStart={-1}
              loadMore={loadTestOutcomes}
              hasMore={hasMore}
              loader={<LoadingBlock message={t('Loading')} key="loading" />}
              useWindow={false}
            >
              <Table
                columns={columns}
                data={testOutcomes}
                alternateRows
                stickyHeader
                stickedHeader={stickedHeader}
                isEmpty={isEmpty}
                emptyMessage={t(
                  `There's nothing here. Try creating a new {{ itemName }}.`,
                  { itemName: t('test') }
                )}
              />
            </InfiniteScroll>
          )}
        </InfiniteListWrapper>
      </div>
    </div>
  )
}
